/**
 * Enterprise-Grade Access Policy Service
 *
 * Centralized access control management with support for:
 * - Role-Based Access Control (RBAC)
 * - Permission-Based Access Control
 * - Attribute-Based Access Control (ABAC)
 * - Policy-driven route protection
 */
import { Role, Permission, AccessMode, UserAttributes, AccessPolicy, AccessCondition, ConditionTree, ConditionGroup, RouteAccessPolicy, AccessEvaluationResult, AccessPolicyRegistry, AccessMonitoringEvent, PolicyConfiguration, CombiningAlgorithm, PolicyDecision, ABACOperator, OperatorRegistry, CacheStats } from '../types/accessControl';
import RoleService from './RoleService';
import AuditService from './AuditService';
import { logger } from "../utils/logger";
// import { logger } from "../utils/logger";
class AccessPolicyService {
    private static policyRegistry: AccessPolicyRegistry = {
        global: [],
        routes: [],
        defaults: {
            requireAuth: true,
            defaultAction: 'deny'
        }
    };
    private static configuration: PolicyConfiguration = {
        enableABAC: true,
        enableMonitoring: true,
        cache: {
            enabled: true,
            ttl: 300000, // 5 minutes
            maxSize: 1000,
            evictionStrategy: 'lru'
        },
        debug: {
            enabled: import.meta.env.DEV,
            logLevel: 'info'
        },
        defaultCombiningAlgorithm: 'deny_overrides',
        enableValidation: true,
        enableDryRun: false
    };
    private static evaluationCache = new Map<string, AccessEvaluationResult>();
    private static cacheStats = { hits: 0, misses: 0, evictions: 0 };
    // Operator registry for extensible ABAC
    private static operatorRegistry: OperatorRegistry = {
        equals: (a: any, b: any) => a === b,
        not_equals: (a: any, b: any) => a !== b,
        in: (a: any, b: any) => Array.isArray(b) && b.includes(a),
        not_in: (a: any, b: any) => Array.isArray(b) && !b.includes(a),
        greater_than: (a: any, b: any) => typeof a === 'number' && a > b,
        less_than: (a: any, b: any) => typeof a === 'number' && a < b,
        greater_equal: (a: any, b: any) => typeof a === 'number' && a >= b,
        less_equal: (a: any, b: any) => typeof a === 'number' && a <= b,
        contains: (a: any, b: any) => typeof a === 'string' && a.includes(b),
        starts_with: (a: any, b: any) => typeof a === 'string' && a.startsWith(b),
        ends_with: (a: any, b: any) => typeof a === 'string' && a.endsWith(b),
        regex: (a: any, b: any) => typeof a === 'string' && new RegExp(b).test(a),
        exists: (a: any) => a !== undefined && a !== null,
        not_exists: (a: any) => a === undefined || a === null,
        between: (a: any, b: any) => typeof a === 'number' && Array.isArray(b) && b.length === 2 && a >= b[0] && a <= b[1],
        before: (a: any, b: any) => new Date(a) < new Date(b),
        after: (a: any, b: any) => new Date(a) > new Date(b),
        memberof: (a: any, b: any) => Array.isArray(a) && a.includes(b),
        subset: (a: any, b: any) => Array.isArray(a) && Array.isArray(b) && a.every(item => b.includes(item)),
        superset: (a: any, b: any) => Array.isArray(a) && Array.isArray(b) && b.every(item => a.includes(item))
    };
    /**
     * Initialize the access policy service with a policy registry
     * @param registry - Access policy registry
     * @param config - Policy configuration
     */
    public static initialize(registry: AccessPolicyRegistry, config?: Partial<PolicyConfiguration>): void {
        this.policyRegistry = registry;
        this.configuration = { ...this.configuration, ...config };
        if (this.configuration.debug?.enabled) {
            logger.debug('[AccessPolicyService] Initialized with policies:', { "arg0": {
                    globalPolicies: registry.global.length,
                    routePolicies: registry.routes.length,
                    configuration: this.configuration
                } });
        }
    }
    /**
     * Register a custom ABAC operator
     * @param operator - Operator name
     * @param evaluator - Function to evaluate the condition
     */
    public static registerOperator(operator: string, evaluator: (attributeValue: any, conditionValue: any) => boolean): void {
        this.operatorRegistry[operator] = evaluator;
        if (this.configuration.debug?.enabled) {
            logger.debug(`[AccessPolicyService] Registered custom operator: ${operator}`);
        }
    }
    /**
     * Get all registered operators
     * @returns Array of operator names
     */
    public static getRegisteredOperators(): string[] {
        return Object.keys(this.operatorRegistry);
    }
    /**
     * Evaluate access for a user against a specific resource
     * @param user - User attempting access
     * @param resource - Resource being accessed
     * @param policies - Specific policies to evaluate (optional)
     * @returns Access evaluation result
     */
    public static evaluateAccess(user: UserAttributes | null, resource: string, policies?: AccessPolicy[]): AccessEvaluationResult {
        const startTime = Date.now();
        const correlationId = this.generateCorrelationId();
        try {
            // Handle unauthenticated users
            if (!user) {
                const result: AccessEvaluationResult = {
                    granted: false,
                    reason: 'User not authenticated',
                    evaluatedPolicies: [],
                    userAttributes: {} as UserAttributes,
                    timestamp: new Date().toISOString(),
                    duration: Date.now() - startTime
                };
                this.logMonitoringEvent({
                    type: 'access_denied',
                    user: {} as UserAttributes,
                    resource,
                    result,
                    correlationId,
                    timestamp: new Date().toISOString()
                });
                return result;
            }
            // Check cache first
            const cacheKey = this.generateCacheKey(user, resource, policies);
            if (this.configuration.cache?.enabled) {
                const cached = this.evaluationCache.get(cacheKey);
                if (cached && this.isCacheValid(cached)) {
                    this.cacheStats.hits++;
                    return cached;
                }
                this.cacheStats.misses++;
            }
            // Get policies to evaluate
            const policiesToEvaluate = policies || this.getPoliciesForResource(resource);
            // Evaluate each policy
            const evaluationResult = this.evaluatePolicies(user, policiesToEvaluate, resource);
            // Add timing information
            evaluationResult.duration = Date.now() - startTime;
            evaluationResult.userAttributes = user;
            // Cache the result
            if (this.configuration.cache?.enabled) {
                this.setCacheEntry(cacheKey, evaluationResult);
            }
            // Log monitoring event
            this.logMonitoringEvent({
                type: evaluationResult.granted ? 'access_granted' : 'access_denied',
                user,
                resource,
                result: evaluationResult,
                correlationId,
                timestamp: new Date().toISOString()
            });
            return evaluationResult;
        }
        catch (error) {
            logger.error('[AccessPolicyService] Error evaluating access:', error, {});
            const errorResult: AccessEvaluationResult = {
                granted: false,
                reason: 'Error during access evaluation',
                evaluatedPolicies: [],
                userAttributes: user || {} as UserAttributes,
                timestamp: new Date().toISOString(),
                duration: Date.now() - startTime
            };
            this.logMonitoringEvent({
                type: 'error',
                user: user || {} as UserAttributes,
                resource,
                result: errorResult,
                correlationId,
                timestamp: new Date().toISOString(),
                metadata: { error: error instanceof Error ? error.message : 'Unknown error' }
            });
            return errorResult;
        }
    }
    /**
     * Check if user has access to a resource (convenience method)
     * @param user - User attempting access
     * @param resource - Resource being accessed
     * @param policies - Specific policies to evaluate (optional)
     * @returns True if access is granted
     */
    public static hasAccess(user: UserAttributes | null, resource: string, policies?: AccessPolicy[]): boolean {
        return this.evaluateAccess(user, resource, policies).granted;
    }
    /**
     * Get policies that apply to a specific resource
     * @param resource - Resource path
     * @returns Array of applicable policies
     */
    public static getPoliciesForResource(resource: string): AccessPolicy[] {
        const applicablePolicies: AccessPolicy[] = [];
        // Add global policies
        applicablePolicies.push(...this.policyRegistry.global.filter(p => p.active !== false));
        // Find route-specific policies
        const routePolicy = this.policyRegistry.routes.find(rp => this.matchesRoute(resource, rp.route));
        if (routePolicy) {
            applicablePolicies.push(...routePolicy.policies.filter(p => p.active !== false));
        }
        // Sort by priority (higher priority first)
        return applicablePolicies.sort((a, b) => (b.priority || 0) - (a.priority || 0));
    }
    /**
     * Evaluate a set of policies against a user using combining algorithms
     * @param user - User to evaluate
     * @param policies - Policies to evaluate
     * @param resource - Resource being accessed
     * @returns Evaluation result
     */
    private static evaluatePolicies(user: UserAttributes, policies: AccessPolicy[], resource: string): AccessEvaluationResult {
        const evaluatedPolicies: string[] = [];
        const policyDecisions: PolicyDecision[] = [];
        const combiningAlgorithm = this.getCombiningAlgorithm(policies);
        // Evaluate each policy individually
        for (const policy of policies) {
            const policyStartTime = Date.now();
            const decision = this.evaluateSinglePolicy(user, policy, resource);
            decision.duration = Date.now() - policyStartTime;
            evaluatedPolicies.push(policy.id);
            policyDecisions.push(decision);
        }
        // Apply combining algorithm
        const finalDecision = this.applyCombiningAlgorithm(policyDecisions, combiningAlgorithm);
        // Collect all failed conditions
        const failedConditions = policyDecisions
            .filter(d => d.failedConditions)
            .flatMap(d => d.failedConditions!);
        return {
            granted: finalDecision.decision === 'permit',
            reason: finalDecision.reason,
            evaluatedPolicies,
            failedConditions: failedConditions.length > 0 ? failedConditions : undefined,
            userAttributes: user,
            timestamp: new Date().toISOString(),
            duration: 0, // Will be set by caller
            policyDecisions,
            combiningAlgorithm
        };
    }
    /**
     * Evaluate a single policy against a user
     * @param user - User to evaluate
     * @param policy - Policy to evaluate
     * @param resource - Resource being accessed
     * @returns Policy decision
     */
    private static evaluateSinglePolicy(user: UserAttributes, policy: AccessPolicy, resource: string): PolicyDecision {
        // Check role requirements
        if (policy.roles && policy.roles.length > 0) {
            if (!RoleService.userHasRole(user, policy.roles)) {
                return {
                    policyId: policy.id,
                    decision: 'deny',
                    reason: `User does not have required roles: ${policy.roles.join(', ')}`,
                    duration: 0
                };
            }
        }
        // Check permission requirements
        if (policy.permissions && policy.permissions.length > 0) {
            const hasPermission = policy.permissionMode === 'all'
                ? RoleService.userHasAllPermissions(user, policy.permissions)
                : RoleService.userHasAnyPermission(user, policy.permissions);
            if (!hasPermission) {
                return {
                    policyId: policy.id,
                    decision: 'deny',
                    reason: `User does not have required permissions: ${policy.permissions.join(', ')}`,
                    duration: 0
                };
            }
        }
        // Check ABAC conditions (legacy flat conditions)
        if (this.configuration.enableABAC && policy.conditions) {
            const conditionResult = this.evaluateConditions(user, policy.conditions);
            if (!conditionResult.granted) {
                return {
                    policyId: policy.id,
                    decision: 'deny',
                    reason: `User does not meet attribute-based conditions`,
                    failedConditions: conditionResult.failedConditions,
                    duration: 0
                };
            }
        }
        // Check ABAC condition tree (enhanced nested conditions)
        if (this.configuration.enableABAC && policy.conditionTree) {
            const conditionResult = this.evaluateConditionTree(user, policy.conditionTree);
            if (!conditionResult.granted) {
                return {
                    policyId: policy.id,
                    decision: 'deny',
                    reason: `User does not meet condition tree requirements`,
                    failedConditions: conditionResult.failedConditions,
                    duration: 0
                };
            }
        }
        // If we get here, policy allows access
        return {
            policyId: policy.id,
            decision: 'permit',
            reason: 'Policy satisfied',
            duration: 0
        };
    }
    /**
     * Get combining algorithm for policy set
     * @param policies - Policies to evaluate
     * @returns Combining algorithm to use
     */
    private static getCombiningAlgorithm(policies: AccessPolicy[]): CombiningAlgorithm {
        // Check if any policy specifies a combining algorithm
        const policyAlgorithm = policies.find(p => p.combiningAlgorithm)?.combiningAlgorithm;
        if (policyAlgorithm)
            return policyAlgorithm;
        // Check registry default
        if (this.policyRegistry.combiningAlgorithm)
            return this.policyRegistry.combiningAlgorithm;
        // Check registry defaults
        if (this.policyRegistry.defaults.combiningAlgorithm)
            return this.policyRegistry.defaults.combiningAlgorithm;
        // Use configuration default
        return this.configuration.defaultCombiningAlgorithm || 'deny_overrides';
    }
    /**
     * Apply combining algorithm to policy decisions
     * @param decisions - Individual policy decisions
     * @param algorithm - Combining algorithm to use
     * @returns Final decision
     */
    private static applyCombiningAlgorithm(decisions: PolicyDecision[], algorithm: CombiningAlgorithm): PolicyDecision {
        const permits = decisions.filter(d => d.decision === 'permit');
        const denies = decisions.filter(d => d.decision === 'deny');
        const notApplicable = decisions.filter(d => d.decision === 'not_applicable');
        switch (algorithm) {
            case 'deny_overrides':
                if (denies.length > 0) {
                    return {
                        policyId: 'combined',
                        decision: 'deny',
                        reason: `Deny overrides: ${denies.map(d => d.reason).join('; ')}`,
                        duration: 0
                    };
                }
                if (permits.length > 0) {
                    return {
                        policyId: 'combined',
                        decision: 'permit',
                        reason: 'Permit granted',
                        duration: 0
                    };
                }
                break;
            case 'permit_overrides':
                if (permits.length > 0) {
                    return {
                        policyId: 'combined',
                        decision: 'permit',
                        reason: 'Permit overrides',
                        duration: 0
                    };
                }
                if (denies.length > 0) {
                    return {
                        policyId: 'combined',
                        decision: 'deny',
                        reason: `Deny: ${denies.map(d => d.reason).join('; ')}`,
                        duration: 0
                    };
                }
                break;
            case 'first_applicable':
                const firstApplicable = decisions.find(d => d.decision !== 'not_applicable');
                if (firstApplicable) {
                    return {
                        ...firstApplicable,
                        policyId: 'combined'
                    };
                }
                break;
            case 'consensus':
                if (permits.length > denies.length) {
                    return {
                        policyId: 'combined',
                        decision: 'permit',
                        reason: 'Consensus: majority permit',
                        duration: 0
                    };
                }
                else if (denies.length > permits.length) {
                    return {
                        policyId: 'combined',
                        decision: 'deny',
                        reason: 'Consensus: majority deny',
                        duration: 0
                    };
                }
                // Tie goes to deny
                return {
                    policyId: 'combined',
                    decision: 'deny',
                    reason: 'Consensus: tie, defaulting to deny',
                    duration: 0
                };
            case 'unanimous':
                if (permits.length === decisions.length && permits.length > 0) {
                    return {
                        policyId: 'combined',
                        decision: 'permit',
                        reason: 'Unanimous permit',
                        duration: 0
                    };
                }
                return {
                    policyId: 'combined',
                    decision: 'deny',
                    reason: 'Not unanimous',
                    duration: 0
                };
            case 'ordered_deny_overrides':
                // Process in order, deny overrides but respect sequence
                for (const decision of decisions) {
                    if (decision.decision === 'deny') {
                        return {
                            ...decision,
                            policyId: 'combined'
                        };
                    }
                }
                if (permits.length > 0) {
                    return {
                        policyId: 'combined',
                        decision: 'permit',
                        reason: 'Ordered permit',
                        duration: 0
                    };
                }
                break;
            case 'ordered_permit_overrides':
                // Process in order, permit overrides but respect sequence
                for (const decision of decisions) {
                    if (decision.decision === 'permit') {
                        return {
                            ...decision,
                            policyId: 'combined'
                        };
                    }
                }
                if (denies.length > 0) {
                    return {
                        policyId: 'combined',
                        decision: 'deny',
                        reason: 'Ordered deny',
                        duration: 0
                    };
                }
                break;
        }
        // Default: no applicable policies
        return {
            policyId: 'combined',
            decision: 'not_applicable',
            reason: 'No applicable policies',
            duration: 0
        };
    }
    /**
     * Evaluate ABAC conditions (legacy flat structure)
     * @param user - User to evaluate
     * @param conditions - Conditions to check
     * @returns Condition evaluation result
     */
    private static evaluateConditions(user: UserAttributes, conditions: AccessCondition[]): {
        granted: boolean;
        failedConditions?: AccessCondition[];
    } {
        const failedConditions: AccessCondition[] = [];
        let lastResult = true;
        for (const condition of conditions) {
            const conditionResult = this.evaluateCondition(user, condition);
            if (condition.logicalOperator === 'OR') {
                if (conditionResult || lastResult) {
                    lastResult = true;
                }
                else {
                    lastResult = false;
                }
            }
            else { // AND (default)
                if (conditionResult && lastResult) {
                    lastResult = true;
                }
                else {
                    lastResult = false;
                    if (!conditionResult) {
                        failedConditions.push(condition);
                    }
                }
            }
        }
        return {
            granted: lastResult,
            failedConditions: failedConditions.length > 0 ? failedConditions : undefined
        };
    }
    /**
     * Evaluate condition tree (enhanced nested structure)
     * @param user - User to evaluate
     * @param tree - Condition tree to evaluate
     * @returns Condition evaluation result
     */
    private static evaluateConditionTree(user: UserAttributes, tree: ConditionTree): {
        granted: boolean;
        failedConditions?: AccessCondition[];
    } {
        if ('attribute' in tree) {
            // It's a leaf condition
            const result = this.evaluateCondition(user, tree);
            return {
                granted: result,
                failedConditions: result ? undefined : [tree]
            };
        }
        else {
            // It's a condition group
            const group = tree as ConditionGroup;
            const results: {
                granted: boolean;
                failedConditions?: AccessCondition[];
            }[] = [];
            for (const child of group.children) {
                results.push(this.evaluateConditionTree(user, child));
            }
            const allGranted = results.every(r => r.granted);
            const anyGranted = results.some(r => r.granted);
            const allFailedConditions = results
                .filter(r => r.failedConditions)
                .flatMap(r => r.failedConditions!);
            if (group.operator === 'AND') {
                return {
                    granted: allGranted,
                    failedConditions: allFailedConditions.length > 0 ? allFailedConditions : undefined
                };
            }
            else { // OR
                return {
                    granted: anyGranted,
                    failedConditions: anyGranted ? undefined : allFailedConditions
                };
            }
        }
    }
    /**
     * Evaluate a single ABAC condition using the operator registry
     * @param user - User to evaluate
     * @param condition - Condition to check
     * @returns True if condition is satisfied
     */
    private static evaluateCondition(user: UserAttributes, condition: AccessCondition): boolean {
        const attributeValue = user[condition.attribute];
        const operator = this.operatorRegistry[condition.operator];
        if (!operator) {
            logger.warn(`[AccessPolicyService] Unknown operator: ${condition.operator}`);
            return false;
        }
        try {
            return operator(attributeValue, condition.value);
        }
        catch (error) {
            logger.error(`[AccessPolicyService] Error evaluating condition:`, error, {});
            return false;
        }
    }
    /**
     * Check if a resource matches a route pattern
     * @param resource - Resource path
     * @param pattern - Route pattern
     * @returns True if resource matches pattern
     */
    private static matchesRoute(resource: string, pattern: string): boolean {
        // Simple wildcard matching - can be enhanced for more complex patterns
        if (pattern === '*')
            return true;
        if (pattern === resource)
            return true;
        // Handle wildcard patterns
        if (pattern.endsWith('*')) {
            const prefix = pattern.slice(0, -1);
            return resource.startsWith(prefix);
        }
        return false;
    }
    /**
     * Generate cache key for evaluation result
     * @param user - User attributes
     * @param resource - Resource path
     * @param policies - Policies being evaluated
     * @returns Cache key
     */
    private static generateCacheKey(user: UserAttributes, resource: string, policies?: AccessPolicy[]): string {
        const policyIds = policies?.map(p => p.id).sort().join(',') || 'default';
        return `${user.id}:${user.role}:${resource}:${policyIds}`;
    }
    /**
     * Check if cached result is still valid
     * @param result - Cached evaluation result
     * @returns True if cache is valid
     */
    private static isCacheValid(result: AccessEvaluationResult): boolean {
        if (!this.configuration.cache?.enabled)
            return false;
        const cacheAge = Date.now() - new Date(result.timestamp).getTime();
        return cacheAge < (this.configuration.cache.ttl || 300000);
    }
    /**
     * Generate correlation ID for tracing
     * @returns Correlation ID
     */
    private static generateCorrelationId(): string {
        return `access_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }
    /**
     * Log monitoring event
     * @param event - Monitoring event
     */
    private static logMonitoringEvent(event: AccessMonitoringEvent): void {
        if (!this.configuration.enableMonitoring)
            return;
        try {
            // Log to audit service for both success and failure
            if (event.type === 'access_denied' || event.type === 'access_granted') {
                AuditService.logAccessControl({
                    user: event.user,
                    resource: event.resource,
                    reason: event.result.reason,
                    metadata: {
                        ...event.metadata,
                        correlationId: event.correlationId,
                        evaluatedPolicies: event.result.evaluatedPolicies,
                        failedConditions: event.result.failedConditions,
                        policyDecisions: event.result.policyDecisions,
                        combiningAlgorithm: event.result.combiningAlgorithm,
                        duration: event.result.duration
                    }
                });
            }
            // Send to external monitoring service if configured
            if (this.configuration.monitoring) {
                this.sendToMonitoringService(event);
            }
            // Debug logging
            if (this.configuration.debug?.enabled) {
                logger.debug('[AccessPolicyService]', { "arg0": event });
            }
        }
        catch (error) {
            logger.error('[AccessPolicyService] Error logging monitoring event:', error, {});
        }
    }
    /**
     * Send event to external monitoring service
     * @param event - Monitoring event
     */
    private static async sendToMonitoringService(event: AccessMonitoringEvent): Promise<void> {
        try {
            const { monitoring } = this.configuration;
            if (!monitoring)
                return;
            // In a real implementation, this would send to the configured service
            // For now, we'll just log the intent
            logger.debug(`[AccessPolicyService] Would send to ${monitoring.service}:`, { "arg0": event });
        }
        catch (error) {
            logger.error('[AccessPolicyService] Error sending to monitoring service:', error, {});
        }
    }
    /**
     * Set cache entry with eviction handling
     * @param key - Cache key
     * @param value - Cache value
     */
    private static setCacheEntry(key: string, value: AccessEvaluationResult): void {
        const maxSize = this.configuration.cache?.maxSize || 1000;
        // Check if we need to evict entries
        if (this.evaluationCache.size >= maxSize) {
            this.evictCacheEntries();
        }
        this.evaluationCache.set(key, value);
    }
    /**
     * Evict cache entries based on strategy
     */
    private static evictCacheEntries(): void {
        const strategy = this.configuration.cache?.evictionStrategy || 'lru';
        const evictCount = Math.floor(this.evaluationCache.size * 0.1); // Evict 10%
        switch (strategy) {
            case 'lru':
                // Simple LRU: remove oldest entries (Map maintains insertion order)
                const keysToEvict = Array.from(this.evaluationCache.keys()).slice(0, evictCount);
                keysToEvict.forEach(key => this.evaluationCache.delete(key));
                this.cacheStats.evictions += evictCount;
                break;
            case 'fifo':
                // FIFO: same as LRU for Map
                const fifoKeys = Array.from(this.evaluationCache.keys()).slice(0, evictCount);
                fifoKeys.forEach(key => this.evaluationCache.delete(key));
                this.cacheStats.evictions += evictCount;
                break;
            case 'lfu':
                // LFU: would need frequency tracking, for now use LRU
                const lfuKeys = Array.from(this.evaluationCache.keys()).slice(0, evictCount);
                lfuKeys.forEach(key => this.evaluationCache.delete(key));
                this.cacheStats.evictions += evictCount;
                break;
        }
    }
    /**
     * Clear evaluation cache
     */
    public static clearCache(): void {
        this.evaluationCache.clear();
        this.cacheStats = { hits: 0, misses: 0, evictions: 0 };
        logger.debug('[AccessPolicyService] Cache cleared');
    }
    /**
     * Get cache statistics
     * @returns Cache statistics
     */
    public static getCacheStats(): CacheStats {
        const total = this.cacheStats.hits + this.cacheStats.misses;
        return {
            size: this.evaluationCache.size,
            hits: this.cacheStats.hits,
            misses: this.cacheStats.misses,
            hitRate: total > 0 ? this.cacheStats.hits / total : 0,
            evictions: this.cacheStats.evictions
        };
    }
    /**
     * Validate a policy for correctness
     * @param policy - Policy to validate
     * @returns Validation result
     */
    public static validatePolicy(policy: AccessPolicy): {
        valid: boolean;
        errors: string[];
    } {
        const errors: string[] = [];
        // Check required fields
        if (!policy.id)
            errors.push('Policy ID is required');
        if (!policy.name)
            errors.push('Policy name is required');
        // Check for conflicting conditions
        if (policy.conditions && policy.conditionTree) {
            errors.push('Cannot have both legacy conditions and conditionTree');
        }
        // Validate condition tree if present
        if (policy.conditionTree) {
            const treeErrors = this.validateConditionTree(policy.conditionTree);
            errors.push(...treeErrors);
        }
        // Validate legacy conditions if present
        if (policy.conditions) {
            const conditionErrors = this.validateConditions(policy.conditions);
            errors.push(...conditionErrors);
        }
        // Check for valid combining algorithm
        if (policy.combiningAlgorithm) {
            const validAlgorithms: CombiningAlgorithm[] = [
                'deny_overrides', 'permit_overrides', 'first_applicable',
                'consensus', 'unanimous', 'ordered_deny_overrides', 'ordered_permit_overrides'
            ];
            if (!validAlgorithms.includes(policy.combiningAlgorithm)) {
                errors.push(`Invalid combining algorithm: ${policy.combiningAlgorithm}`);
            }
        }
        return {
            valid: errors.length === 0,
            errors
        };
    }
    /**
     * Validate condition tree structure
     * @param tree - Condition tree to validate
     * @returns Array of validation errors
     */
    private static validateConditionTree(tree: ConditionTree): string[] {
        const errors: string[] = [];
        if ('attribute' in tree) {
            // It's a leaf condition
            if (!tree.attribute)
                errors.push('Condition attribute is required');
            if (!tree.operator)
                errors.push('Condition operator is required');
            if (!this.operatorRegistry[tree.operator]) {
                errors.push(`Unknown operator: ${tree.operator}`);
            }
        }
        else {
            // It's a condition group
            const group = tree as ConditionGroup;
            if (!group.operator || !['AND', 'OR'].includes(group.operator)) {
                errors.push('Condition group must have AND or OR operator');
            }
            if (!group.children || group.children.length === 0) {
                errors.push('Condition group must have children');
            }
            // Recursively validate children
            group.children.forEach((child, index) => {
                const childErrors = this.validateConditionTree(child);
                childErrors.forEach(error => errors.push(`Child ${index}: ${error}`));
            });
        }
        return errors;
    }
    /**
     * Validate legacy conditions
     * @param conditions - Conditions to validate
     * @returns Array of validation errors
     */
    private static validateConditions(conditions: AccessCondition[]): string[] {
        const errors: string[] = [];
        conditions.forEach((condition, index) => {
            if (!condition.attribute)
                errors.push(`Condition ${index}: attribute is required`);
            if (!condition.operator)
                errors.push(`Condition ${index}: operator is required`);
            if (!this.operatorRegistry[condition.operator]) {
                errors.push(`Condition ${index}: unknown operator ${condition.operator}`);
            }
        });
        return errors;
    }
    /**
     * Dry-run policy evaluation (evaluation without enforcement)
     * @param user - User to evaluate
     * @param resource - Resource being accessed
     * @param policies - Specific policies to evaluate (optional)
     * @returns Evaluation result with dry-run flag
     */
    public static dryRunEvaluation(user: UserAttributes | null, resource: string, policies?: AccessPolicy[]): AccessEvaluationResult & {
        dryRun: boolean;
    } {
        if (!this.configuration.enableDryRun) {
            throw new Error('Dry-run mode is not enabled');
        }
        const result = this.evaluateAccess(user, resource, policies);
        return { ...result, dryRun: true };
    }
}
export default AccessPolicyService;
